import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { useIsAdminDockNavVisible } from "@circle-react/components/CircleAi/Dock/useIsAdminDockNavVisible";
import { useIsCircleAiEnabled } from "@circle-react/components/CircleAi/useCircleAiFeatureFlags";
import {
  useCurrentCommunityMember,
  useCurrentUser,
  useIsEligibleForNewOnboardingFlow,
  usePunditUserContext,
} from "@circle-react/contexts/punditUserContext";
import { createNewCommunityPath } from "@circle-react/helpers/urlHelpers";
import { useIsCustomColorTheme } from "@circle-react/hooks/useIsCustomColorTheme";
import type { CommunitySwitcher } from "@circle-react/types/CommunitySwitcher";
import type { CurrentCommunity } from "@circle-react/types/CurrentCommunity";
import type { CommunitySwitcherContextValue } from "@circleco/community-switcher/context/CommunitySwitcherContext";
import type { CommunitySwitcherApi } from "@circleco/community-switcher/types/adapters";
import { useLocation } from "@tanstack/react-router";
import type { MouseEvent } from "react";
import { useCallback, useMemo } from "react";

import { t } from "@/i18n-js/instance";
import { useCreateLoginToken } from "@/react/components/DiscoverComponent";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";
import { useCommunitySwitcher } from "@/react/hooks/community_switcher/useCommunitySwitcherApi";
import { useIsMemberAreaAppShellEnabled } from "@/react/hooks/useIsMemberAreaAppShellEnabled";
import { useTailwindLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import { useThemeContext } from "@/react/providers/Theme";

import {
  useCustomSidebarBackgroundColor,
  useCustomSidebarBorderColor,
} from "../Spaces/SidebarV2/hooks";
import { useCircleAiIntroSession } from "./useCircleAiIntroSession";
import { useCommunitySwitcherStyles } from "./useCommunitySwitcherStyles";
import { useSwitcherNotificationCounts } from "./useSwitcherNotificationCounts";
import {
  buildNewCommunityHrefWithPendo,
  DISCOVER_ROUTE,
  getDiscoverLoginUrl,
  notifyCommunitySwitch,
  shouldUsePendoOnNewCommunityLink,
} from "./utils";

export const useCommunitySwitcherAdapter = (
  isLoadingAppData?: boolean,
): {
  providerValue: CommunitySwitcherContextValue;
} => {
  const { pathname, searchStr, hash } = useLocation({
    select: location => ({
      pathname: location.pathname,
      searchStr: location.searchStr,
      hash: location.hash,
    }),
  });
  const {
    communities,
    token,
    isLoadingCommunities,
    isReorderingCommunities,
    onSortCommunities,
  } = useCommunitySwitcher();
  const themeContext = useThemeContext();
  const styles = useCommunitySwitcherStyles();
  const { currentCommunity } = usePunditUserContext();
  const currentCommunityMember = useCurrentCommunityMember();
  const currentUser = useCurrentUser();
  const isEligibleForNewOnboardingFlow = useIsEligibleForNewOnboardingFlow();
  const { electronAPI, isElectron } = useElectronAPI();
  const isCustomColorTheme = useIsCustomColorTheme();
  const customSidebarBorderColor = useCustomSidebarBorderColor();
  const customSidebarBackgroundColor = useCustomSidebarBackgroundColor();
  const isLargeScreen = useTailwindLgScreenMediaQuery();
  const { notificationCounts, setCommunitiesNeedingCounts } =
    useSwitcherNotificationCounts();
  const { trackEvent } = useEventsTracker();

  const shouldShowPendoUrl = shouldUsePendoOnNewCommunityLink(
    currentCommunityMember?.is_admin,
    pathname,
    currentCommunity?.plan_status,
  );

  const parseCurrentCommunity = (
    community?: CurrentCommunity,
  ): CommunitySwitcherApi["currentCommunity"] => {
    if (!community) return undefined;
    return {
      is_current_community: true,
      // props with distinct names
      light_logo_url: community.logo_url,
      light_icon_url: community.icon_url,
      root_url: community.full_url,
      // shared props
      id: community.id,
      name: community.name,
      dark_icon_url: community.dark_icon_url,
      dark_logo_url: community.dark_logo_url,
    };
  };

  const newCommunityHref = useMemo(() => {
    const baseHref = shouldShowPendoUrl
      ? buildNewCommunityHrefWithPendo(pathname, searchStr, hash)
      : createNewCommunityPath();

    const url = new URL(baseHref, globalThis.location.origin);
    url.searchParams.set("onboarding_entry_point", "community_switcher");
    return url.toString();
  }, [shouldShowPendoUrl, pathname, searchStr, hash]);

  const api: CommunitySwitcherApi = useMemo(
    () => ({
      communities,
      currentCommunity: parseCurrentCommunity(currentCommunity),
      token,
      isLoadingCommunities,
      isReorderingCommunities,
      onSortCommunities,
    }),
    [
      communities,
      currentCommunity,
      token,
      isLoadingCommunities,
      isReorderingCommunities,
      onSortCommunities,
    ],
  );

  const theme = useMemo(
    () => ({
      isDarkAppearance: Boolean(themeContext.isDarkAppearance),
      isLargeScreen,
      presentationClasses: styles.presentationClasses,
      bodyClasses: styles.bodyClasses,
      bodyStyles: styles.bodyStyles,
      dividerColor: isCustomColorTheme ? customSidebarBorderColor : undefined,
    }),
    [
      themeContext.isDarkAppearance,
      isLargeScreen,
      styles.presentationClasses,
      styles.bodyClasses,
      styles.bodyStyles,
      isCustomColorTheme,
      customSidebarBorderColor,
    ],
  );

  const backgroundColor = isCustomColorTheme
    ? customSidebarBackgroundColor
    : undefined;

  const isCommunitySwitcherEnabled = Boolean(
    currentCommunity?.community_switcher_enabled,
  );

  // The new dock nav exposes its own Circle AI entry, so only surface the
  // switcher's Circle AI button when that nav is not visible (i.e. the member
  // area / community switcher experience).
  const isAdminDockNavVisible = useIsAdminDockNavVisible();
  const isMemberAreaAppShellEnabled = useIsMemberAreaAppShellEnabled();
  const isCircleAiEnabled = useIsCircleAiEnabled();
  // The Circle AI entry only makes sense inside the app shell, where the
  // conversation panel is mounted to respond to `onCircleAiClick`. Outside the
  // shell (legacy member area) we must leave the switcher untouched.
  const isCircleAiSwitcherEntryVisible =
    isMemberAreaAppShellEnabled && isCircleAiEnabled;
  const isAdmin = Boolean(currentCommunityMember?.is_admin);

  const isCircleAiButtonVisible =
    isAdmin && isCircleAiSwitcherEntryVisible && !isAdminDockNavVisible;

  const { mutate: generateDiscoverLoginToken } = useCreateLoginToken({
    onSuccess: data => {
      if (!data.login_url) return;
      globalThis.location.href = getDiscoverLoginUrl(
        data.login_url,
        currentCommunity?.id,
      );
    },
  });

  const onDiscoverClick = (e: MouseEvent) => {
    e?.stopPropagation();
    generateDiscoverLoginToken();
  };

  const onCreateCommunityButtonClick = useCallback(() => {
    trackEvent(
      "community_switcher.create_community.clicked",
      { is_pendo_url: shouldShowPendoUrl },
      { batch: true },
    );
  }, [trackEvent, shouldShowPendoUrl]);

  const onCreateCommunity = useCallback(
    (name: string) => {
      trackEvent(
        "community_switcher.create_community.submitted",
        { is_pendo_url: shouldShowPendoUrl },
        { batch: true },
      );
      const separator = newCommunityHref.includes("?") ? "&" : "?";
      globalThis.location.href = `${newCommunityHref}${separator}name=${encodeURIComponent(name)}`;
    },
    [newCommunityHref, trackEvent, shouldShowPendoUrl],
  );

  const newCommunityDefaultName = currentUser?.first_name
    ? `${currentUser.first_name}'s ${t("create_community_modal.default_name_suffix")}`
    : undefined;

  const onCircleAiClick = useCallback(() => {
    const store = useCircleAIShellStore.getState();
    const willOpen = store.conversationPanelState === "closed";
    store.setConversationPanelState(willOpen ? "default" : "closed");
    // Don't flip the persisted mode here: an admin opening Circle AI from the
    // member-side switcher should keep their "community" mode. The admin nav is
    // surfaced transiently via `isAdminDockForced` while the panel is open, so
    // closing it reverts the dock to the community switcher rather than getting
    // stuck in admin mode.
    store.setAdminDockForced(isAdmin ? willOpen : false);
    // `isAdmin` comes from the pundit context (render scope), not the shell
    // store, so it must stay in the deps; the shell store reads use getState()
    // because they only need the latest value at click time.
  }, [isAdmin]);

  const circleAiIntro = useCircleAiIntroSession(currentUser?.id);
  const isUserAnyCommunityMember =
    Boolean(currentUser) && Boolean(communities?.length);

  const providerValue = useMemo(
    (): CommunitySwitcherContextValue => ({
      api,
      featureFlags: {
        discoverLinkTo: DISCOVER_ROUTE,
        onDiscoverClick,
        isElectron,
        newCommunityHref,
        onElectronChangeCommunity: (params: { name?: string; href?: string }) =>
          electronAPI?.changeCommunity(params),
        showDiscoverButton: isCommunitySwitcherEnabled,
        showNewCommunityButton: isCommunitySwitcherEnabled,
        onCreateCommunity: isEligibleForNewOnboardingFlow
          ? onCreateCommunity
          : undefined,
        onCreateCommunityButtonClick,
        newCommunityDefaultName,
        showCommunities: isCommunitySwitcherEnabled,
        showSidebar:
          isUserAnyCommunityMember &&
          (isCommunitySwitcherEnabled || isCircleAiSwitcherEntryVisible),
        showCircleAiButton: isCircleAiButtonVisible,
        ...circleAiIntro,
        onCircleAiClick,
        onCommunitySwitch: notifyCommunitySwitch,
        // NOTE: The Circle AI dock needs the Discover logo at 36px and the
        // presentation without the fixed `w-18` width. Those tweaks live in the
        // `@circleco/community-switcher` package, which is owned elsewhere and
        // must not be modified here, so they're intentionally not wired up.
        // Coordinate with the package owners to add gated feature flags
        // (e.g. `largeDiscoverIcon` / `fullWidthPresentation`) if needed.
      },
      backgroundColor,
      setCommunitiesNeedingCounts,
      isLoadingAppData,
      getNotificationCount: (community: CommunitySwitcher) =>
        notificationCounts[community.id] ?? 0,
      t,
      theme,
    }),
    [
      api,
      backgroundColor,
      electronAPI,
      isElectron,
      isLoadingAppData,
      newCommunityHref,
      onCreateCommunity,
      onCreateCommunityButtonClick,
      isEligibleForNewOnboardingFlow,
      newCommunityDefaultName,
      notificationCounts,
      setCommunitiesNeedingCounts,
      isCommunitySwitcherEnabled,
      isUserAnyCommunityMember,
      isCircleAiSwitcherEntryVisible,
      isCircleAiButtonVisible,
      circleAiIntro,
      isAdminDockNavVisible,
      onCircleAiClick,
      onDiscoverClick,
      theme,
    ],
  );

  return { providerValue };
};
